/**
 * The offline queue (DESIGN.md §12.8).
 *
 * A phone at a restaurant table often has no signal. An entry created in that state is
 * stored in IndexedDB with a client-generated idempotency key and replayed on reconnect.
 * The server honours `Idempotency-Key`, so a replay that crosses with a request that
 * actually succeeded returns the original entry rather than charging anyone twice.
 */

import { ApiError, api, newIdempotencyKey } from "./api";

const DB_NAME = "splitwell";
const DB_VERSION = 1;
const STORE = "queued-entries";

export interface QueuedEntry {
  key: string;
  groupId: number;
  payload: unknown;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE)) {
        database.createObjectStore(STORE, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const database = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    database.close();
  }
}

export async function enqueue(groupId: number, payload: unknown, key?: string): Promise<string> {
  const entry: QueuedEntry = {
    key: key ?? newIdempotencyKey(),
    groupId,
    payload,
    createdAt: Date.now(),
    attempts: 0,
  };
  await withStore("readwrite", (store) => store.put(entry));
  return entry.key;
}

export async function queued(): Promise<QueuedEntry[]> {
  try {
    const entries = await withStore<QueuedEntry[]>("readonly", (store) => store.getAll());
    return entries.sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [];
  }
}

export async function remove(key: string): Promise<void> {
  await withStore("readwrite", (store) => store.delete(key));
}

async function markAttempt(entry: QueuedEntry, message: string): Promise<void> {
  await withStore("readwrite", (store) =>
    store.put({ ...entry, attempts: entry.attempts + 1, lastError: message }),
  );
}

export interface FlushResult {
  sent: number;
  remaining: number;
}

/**
 * Replay everything queued. Safe to call repeatedly and from several tabs: the
 * idempotency key means a duplicate send returns the original entry, and the local copy
 * is dropped either way.
 */
export async function flush(): Promise<FlushResult> {
  const entries = await queued();
  let sent = 0;

  for (const entry of entries) {
    try {
      await api.post(`/groups/${entry.groupId}/transactions`, entry.payload, {
        idempotencyKey: entry.key,
      });
      await remove(entry.key);
      sent += 1;
    } catch (cause) {
      if (cause instanceof ApiError) {
        // The server answered. A 4xx will never succeed on retry, so stop carrying it —
        // except a 401, which just means "sign in again first".
        if (cause.status === 401) break;
        if (cause.status < 500) {
          await remove(entry.key);
          continue;
        }
      }
      await markAttempt(entry, cause instanceof Error ? cause.message : "unknown");
      break; // Still offline, or the server is unwell: try again later.
    }
  }

  return { sent, remaining: (await queued()).length };
}

/** Replay on reconnect, and once at startup in case the last session ended offline. */
export function startReplay(onFlush?: (result: FlushResult) => void): () => void {
  let running = false;
  const run = async () => {
    if (running || !navigator.onLine) return;
    running = true;
    try {
      const result = await flush();
      if (result.sent > 0) onFlush?.(result);
    } finally {
      running = false;
    }
  };

  void run();
  window.addEventListener("online", () => void run());
  const timer = window.setInterval(() => void run(), 60_000);
  return () => {
    window.removeEventListener("online", () => void run());
    window.clearInterval(timer);
  };
}

/** True when a failure was the network rather than the server saying no. */
export function isNetworkFailure(error: unknown): boolean {
  return !(error instanceof ApiError);
}
